import { Capacitor } from '@capacitor/core'

/**
 * True only inside the real iOS/Android Capacitor shell.
 * Do NOT use Ionic isPlatform('ios'|'android') — Chrome device emulation fakes those
 * user-agents and would incorrectly force the native Auth0 callback scheme.
 */
export const isNativeApp = (): boolean => Capacitor.isNativePlatform()

/**
 * Scopes requested at login so Auth0 issues a refresh token
 * (rotation + 30-day max lifetime are enabled in the Auth0 dashboard).
 */
export const AUTH0_LOGIN_SCOPE = 'openid profile email read:current_user offline_access'

/**
 * Scope for the API access token (Auth0 Management API audience).
 * Must stay as the Management API scope only — requesting full OIDC scopes here
 * can produce a token the Scrubstr API rejects with 401.
 */
export const AUTH0_API_SCOPE = 'read:current_user'

/** localStorage: onboarding should open after a successful signup redirect */
export const SIGNUP_FLOW_KEY = 'scrubstr_signup_flow'

/** sessionStorage: user cancelled Auth0 (declined consent, access_denied, etc.) */
export const AUTH_CANCELLED_KEY = 'scrubstr_auth_cancelled'

/** localStorage: user subs that have completed onboarding on this device */
export const ONBOARDING_SEEN_KEY = 'scrubstr_onboarded_users'

export const clearSignupFlow = (): void => {
  try {
    localStorage.removeItem(SIGNUP_FLOW_KEY)
  } catch {
    // ignore
  }
}

export const isSignupFlowActive = (): boolean => {
  try {
    return localStorage.getItem(SIGNUP_FLOW_KEY) === 'true'
  } catch {
    return false
  }
}

export const markSignupFlowPending = (): void => {
  try {
    localStorage.setItem(SIGNUP_FLOW_KEY, 'true')
  } catch {
    // ignore
  }
}

export const markAuthCancelled = (): void => {
  try {
    sessionStorage.setItem(AUTH_CANCELLED_KEY, 'true')
  } catch {
    // ignore
  }
}

export const getAuthErrorFromUrl = (): string | null => {
  try {
    return new URLSearchParams(window.location.search).get('error')
  } catch {
    return null
  }
}

export const getAuth0Domain = (): string => process.env.REACT_APP_AUTH0_DOMAIN || 'scrubstr.us.auth0.com'

/** Auth0 logout redirect after user declines consent (access_denied). Must match Allowed Logout URLs (origin only, same as Logout page). */
export const getPostDeclineLogoutReturnTo = (): string => {
  if (isNativeApp()) {
    const domain = getAuth0Domain()
    return `com.scrubstr.app://${domain}/capacitor/com.scrubstr.app/callback`
  }
  // App root redirects to /hospitals — do not use /hospitals here unless that full URL is allow-listed in Auth0.
  return window.location.origin
}

/** Clear signup/onboarding flags when user declines authorization or auth redirect fails. */
export const cleanupAfterAuthDecline = (): void => {
  clearSignupFlow()
  markAuthCancelled()
}
